import type { WeaponHold } from '../render/characters/animation';

/**
 * The offline arsenal, as data (like the vehicle roster in src/vehicles/specs.ts). Kessmouth, 1970:
 * fists and whatever's lying about are common, a blade or a cosh comes from under a counter, and a
 * gun is a rare, expensive, noisy thing that brings the whole station down on you. Tune here; the
 * weapons system and the tests read nothing else. Numbers are documented in docs/WEAPONS.md.
 */

export type WeaponId = 'fists' | 'cosh' | 'bat' | 'knife' | 'crowbar' | 'revolver' | 'sawnoff';
export type WeaponClass = 'unarmed' | 'blunt' | 'blade' | 'pistol' | 'shotgun';

export interface MeleeSpec {
  /** Reach from the player's centre (m). */
  reach: number;
  /** Half-angle of the strike arc in front (rad). */
  arc: number;
  /** Damage per blow (a person has 100). */
  damage: number;
  /** Whole swing, wind-up to recovery (s). Another swing can't start before it ends. */
  swingTime: number;
  /** When in the swing the blow lands (s). */
  hitAt: number;
  /** Shove given to whoever is hit (m/s). */
  knockback: number;
  /** Can this weapon kill? (Fists and a cosh put people down, not in the ground.) */
  lethal: boolean;
  /** Bodywork damage when the swing lands on a car (0 = bounces off). */
  vehicleDamage: number;
  sound: 'punch' | 'thud' | 'slash' | 'clang';
}

export interface FirearmSpec {
  /** Rounds in the cylinder / barrels. */
  capacity: number;
  /** Projectiles per shot (shot pellets). */
  pellets: number;
  /** Damage per projectile at close range. */
  damage: number;
  /** Half-angle of the spread cone (rad). Pellets are spread evenly across it with a little jitter. */
  spread: number;
  /** Max range (m). */
  range: number;
  /** Damage falls off linearly from here to `range` (down to 35 %). */
  falloffStart: number;
  /** Seconds between shots. */
  fireInterval: number;
  /** Full reload (s). */
  reloadTime: number;
  /** Most spare rounds you can carry. */
  maxReserve: number;
  /** Ammunition, as the fence sells it. */
  ammo: { name: string; box: number; price: number };
  /** Camera trauma per shot. */
  recoil: number;
  /** Seconds to bring the gun up. */
  raiseTime: number;
}

export interface WeaponDef {
  id: WeaponId;
  name: string;
  /** Short HUD name. */
  short: string;
  cls: WeaponClass;
  /** Character rig hold style (render/characters). */
  hold: WeaponHold;
  /** Fence price (£); null = not for sale. */
  price: number | null;
  /** Can be picked up off the street (bats in the park, crowbars in the yards). */
  findable: boolean;
  /** Break-in time multiplier on locked cars while this is in hand. */
  breakInScale: number;
  melee: MeleeSpec;
  firearm: FirearmSpec | null;
  /** One line for the fence's list. */
  blurb: string;
}

const PUNCH: MeleeSpec = { reach: 1.25, arc: 0.9, damage: 18, swingTime: 0.42, hitAt: 0.16, knockback: 2.0, lethal: false, vehicleDamage: 0, sound: 'punch' };

export const WEAPONS: Readonly<Record<WeaponId, WeaponDef>> = {
  fists: {
    id: 'fists',
    name: 'Fists',
    short: 'FISTS',
    cls: 'unarmed',
    hold: 'fist',
    price: null,
    findable: false,
    breakInScale: 1,
    melee: PUNCH,
    firearm: null,
    blurb: 'Always on you.',
  },
  cosh: {
    id: 'cosh',
    name: 'Leather cosh',
    short: 'COSH',
    cls: 'blunt',
    hold: 'club',
    price: 12,
    findable: false,
    breakInScale: 1,
    melee: { reach: 1.4, arc: 0.8, damage: 34, swingTime: 0.5, hitAt: 0.2, knockback: 2.6, lethal: false, vehicleDamage: 0, sound: 'thud' },
    firearm: null,
    blurb: 'Lead shot in a leather sock. Puts a man down quiet.',
  },
  bat: {
    id: 'bat',
    name: 'Cricket bat',
    short: 'BAT',
    cls: 'blunt',
    hold: 'club',
    price: 15,
    findable: true,
    breakInScale: 1,
    melee: { reach: 1.85, arc: 1.1, damage: 42, swingTime: 0.72, hitAt: 0.32, knockback: 4.2, lethal: true, vehicleDamage: 7, sound: 'thud' },
    firearm: null,
    blurb: 'Long reach, slow swing. Someone left one in the park.',
  },
  knife: {
    id: 'knife',
    name: 'Flick knife',
    short: 'KNIFE',
    cls: 'blade',
    hold: 'blade',
    price: 10,
    findable: false,
    breakInScale: 1,
    melee: { reach: 1.2, arc: 0.6, damage: 55, swingTime: 0.38, hitAt: 0.14, knockback: 1.4, lethal: true, vehicleDamage: 0, sound: 'slash' },
    firearm: null,
    blurb: 'Quick and close. Very nasty, very illegal.',
  },
  crowbar: {
    id: 'crowbar',
    name: 'Crowbar',
    short: 'CROWBAR',
    cls: 'blunt',
    hold: 'club',
    price: 8,
    findable: true,
    breakInScale: 0.4,
    melee: { reach: 1.7, arc: 0.95, damage: 45, swingTime: 0.66, hitAt: 0.3, knockback: 3.6, lethal: true, vehicleDamage: 9, sound: 'clang' },
    firearm: null,
    blurb: 'Pops a locked door in half the time. Swings heavy.',
  },
  revolver: {
    id: 'revolver',
    name: '.38 revolver',
    short: '.38',
    cls: 'pistol',
    hold: 'pistol',
    price: 250, // was £160 until 2026-10: a gun should cost more than one job pays
    findable: false,
    breakInScale: 1,
    // A pistol-whip if you're out of rounds and someone's in your face.
    melee: { ...PUNCH, damage: 22, sound: 'thud' },
    firearm: {
      capacity: 6,
      pellets: 1,
      damage: 70,
      spread: 0.025,
      range: 38,
      falloffStart: 22,
      fireInterval: 0.42,
      reloadTime: 2.4,
      maxReserve: 30,
      ammo: { name: '.38 rounds', box: 6, price: 24 },
      recoil: 0.2,
      raiseTime: 0.18,
    },
    blurb: 'Six-shot, ex-service, numbers filed off. Comes loaded.',
  },
  sawnoff: {
    id: 'sawnoff',
    name: 'Sawn-off shotgun',
    short: 'SAWN-OFF',
    cls: 'shotgun',
    hold: 'shotgun',
    price: 300, // was £140 until 2026-10: dearer than the .38 — it clears a pub
    findable: false,
    breakInScale: 1,
    melee: { ...PUNCH, reach: 1.35, damage: 26, sound: 'thud' },
    firearm: {
      capacity: 2,
      pellets: 9,
      damage: 24,
      spread: 0.17,
      range: 16,
      falloffStart: 5,
      fireInterval: 0.35,
      reloadTime: 1.9,
      maxReserve: 16,
      ammo: { name: '12-bore cartridges', box: 4, price: 18 },
      recoil: 0.38,
      raiseTime: 0.26,
    },
    blurb: 'Both barrels, short and wide. Nothing clears a pub faster.',
  },
};

/** Cycle order for `weaponNext` (only what you carry). */
export const WEAPON_ORDER: readonly WeaponId[] = ['fists', 'cosh', 'bat', 'crowbar', 'knife', 'revolver', 'sawnoff'];

export function isWeaponId(v: unknown): v is WeaponId {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(WEAPONS, v);
}

export function getWeapon(id: WeaponId): WeaponDef {
  return WEAPONS[id];
}

export const isFirearm = (id: WeaponId): boolean => WEAPONS[id].firearm !== null;

/** People (pedestrians, officers) start with this much; see combat.ts for knockdown / kill rules. */
export const PERSON_HP = 100;
/** A blow at least this hard knocks you off your feet whatever your health. */
export const KNOCKDOWN_DAMAGE = 40;
/** Hurt below this, you go down. */
export const KNOCKDOWN_HP = 50;
/** Health recovers fully this long after the last hit (s). */
export const HP_RECOVER_AFTER = 60;

/** What the fence stocks, in list order (weapons, then ammunition). */
export interface FenceItem {
  /** Stable id used by the shop keys and the `fenceBuy` hook. */
  id: string;
  kind: 'weapon' | 'ammo';
  weapon: WeaponId;
  label: string;
  price: number;
}

export function fenceStock(): FenceItem[] {
  const out: FenceItem[] = [];
  for (const id of WEAPON_ORDER) {
    const w = WEAPONS[id];
    if (w.price !== null) out.push({ id, kind: 'weapon', weapon: id, label: w.name, price: w.price });
  }
  for (const id of WEAPON_ORDER) {
    const f = WEAPONS[id].firearm;
    if (f) out.push({ id: `${id}-ammo`, kind: 'ammo', weapon: id, label: `${f.ammo.name} x${f.ammo.box}`, price: f.ammo.price });
  }
  return out;
}
